import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { 
  MapPin, 
  Lightbulb, 
  ClipboardList, 
  PlayCircle, 
  TrendingUp,
  CheckCircle2,
  X,
  ChevronDown,
  Circle,
  ArrowRight,
  Eye,
  Lock,
  Map as MapIcon,
  Zap,
  Target
} from 'lucide-react';
import { useProjectStore, ProjectStage } from '../store/useProjectStore';
import { useWorkspaceMode, WorkspaceMode } from '../context/WorkspaceModeContext';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { ContextualKPI } from './analytics/ContextualKPI';
import { useAppToast } from './AppToast';
import { useTheme } from '../context/ThemeContext';
import { useJourneyStore } from '../store/useJourneyStore';
import { ActionService, type EnterpriseAction } from '../services/actionService';
import { IndicatorService } from '../services/indicatorService';
import { LinkKpiModal, type CanonicalKpiOption } from './LinkKpiModal';
import { useStep5Evidences, type Step5CtaTarget } from '../hooks/useStep5Evidences';
import type { SustainabilityAudit } from '../types/consultingProject';

export type ProjectStep = 'diagnostico' | 'design' | 'planejamento' | 'execucao' | 'sustentacao';

// Mapeamento entre ProjectStep (UI) e ProjectStage (Store)
const stepToStageMap: Record<ProjectStep, ProjectStage> = {
  'diagnostico': 'diagnosis',
  'design': 'kaizen',
  'planejamento': 'planning',
  'execucao': 'execution',
  'sustentacao': 'sustainment'
};

const stageToStepMap: Record<ProjectStage, ProjectStep> = {
  'diagnosis': 'diagnostico',
  'kaizen': 'design',
  'planning': 'planejamento',
  'execution': 'execucao',
  'sustainment': 'sustentacao'
};

interface ProjectJourneySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  currentStep?: number;
  showDeviationAlert?: boolean;
  showTopChrome?: boolean;
  onStepClick?: (step: ProjectStep) => void;
  onOpenStageTransition?: (currentStage: ProjectStage, nextStage: ProjectStage) => void;
  onGenerateActionPlan?: () => void | Promise<void>;
  step5Reviews?: SustainabilityAudit[];
}

interface StepConfig {
  id: ProjectStep;
  label: string;
  description: string;
  icon: React.ElementType;
}

const STEPS: StepConfig[] = [
  {
    id: 'diagnostico',
    label: 'Diagnóstico e Takt',
    description: 'Coleta de dados e definição do ritmo',
    icon: Target
  },
  {
    id: 'design',
    label: 'Engenharia de Melhoria',
    description: 'Geração de ideias de melhoria e estimativa de redução de NVA',
    icon: MapIcon
  },
  {
    id: 'planejamento',
    label: 'Otimização de Fluxo',
    description: 'Geração automatizada do Mapa Futuro e recálculo de tempos',
    icon: Zap
  },
  {
    id: 'execucao',
    label: 'Desdobramento Estratégico',
    description: 'Cadastro completo de ações, prazos e responsáveis',
    icon: TrendingUp
  },
  {
    id: 'sustentacao',
    label: 'Sustentação e ROI',
    description: 'Monitoramento de resultados reais vs. projetados e gráficos de tendência',
    icon: ClipboardList
  }
];

export const ProjectJourneyHeader: React.FC<{
  currentIndex: number;
  total: number;
  showDeviationAlert?: boolean;
  compact?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
}> = ({ currentIndex, total, showDeviationAlert = false, compact = false, isExpanded = false, onToggle }) => {
  const { isDark } = useTheme();
  const mapType = useProjectStore(state => state.mapType);
  const headerClassName = !isDark
    ? 'border-b border-slate-200 bg-white'
    : 'border-b border-slate-800 bg-slate-900';
  // Compact pill-style header matching design
  if (compact) {
    const step = STEPS[Math.max(0, Math.min(total - 1, currentIndex - 1))];
    const stepLabel = step ? step.label : `Etapa ${currentIndex}`;
    const StepIcon = step?.icon ?? Target;
    const progressWidth = `${(Math.max(1, currentIndex) / total) * 100}%`;
    const containerTone = !isDark
      ? 'border-slate-300 bg-slate-200 text-slate-800'
      : mapType === 'FACTORY'
      ? 'border-indigo-700 bg-indigo-950/95 text-white'
      : 'border-slate-700 bg-slate-900 text-white';
    const iconTone = !isDark
      ? 'border-slate-300 bg-white/70 text-slate-700'
      : mapType === 'FACTORY'
      ? 'border-indigo-600 bg-indigo-900 text-indigo-100'
      : 'border-slate-700 bg-slate-800 text-slate-100';
    const metaTone = !isDark ? 'text-slate-600' : 'text-indigo-200';
    const progressTrack = !isDark ? 'bg-slate-300/90' : 'bg-slate-800/90';
    return (
      <div
        onClick={onToggle}
        className={`relative w-full cursor-pointer overflow-hidden rounded-[18px] border shadow-sm ${containerTone}`}
      >
        <div className="flex items-center gap-3 px-3 py-2.5">
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl border ${iconTone}`}>
            <StepIcon size={16} />
          </div>

          <div className="min-w-0 flex-1">
            <div className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${metaTone}`}>Etapa {currentIndex}</div>
            <div className="truncate text-sm font-semibold">{stepLabel}</div>
          </div>

          <div className={`flex h-9 w-9 items-center justify-center rounded-xl border ${iconTone}`}>
            <ChevronDown size={16} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
          </div>
        </div>
        <div className={`h-[3px] w-full ${progressTrack}`}>
          <div
            className={`h-full rounded-r-full transition-all duration-300 ${showDeviationAlert ? 'bg-amber-400' : 'bg-cyan-400'}`}
            style={{ width: progressWidth }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${compact ? 'px-3 py-2' : 'px-4 py-2'} ${headerClassName} cursor-pointer`} onClick={onToggle}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-[11px] font-semibold tracking-[0.06em] ${showDeviationAlert ? 'text-amber-600 dark:text-amber-300' : !isDark ? 'text-slate-900' : 'text-slate-100'}`}>
            Jornada do Projeto <span className="text-slate-400 font-normal">·</span> ETAPA <span className="font-mono font-bold">{currentIndex}/{total}</span>
          </h3>
          <div className="mt-1 w-full">
            <div className={`h-1 rounded-full overflow-hidden ${showDeviationAlert ? 'ring-1 ring-amber-400/80' : ''} ${!isDark ? 'bg-slate-100' : 'bg-slate-800'}`}>
              <div 
                className={`h-full transition-all duration-500 rounded-full ${showDeviationAlert ? 'bg-amber-400' : 'bg-cyan-400'}`}
                style={{ width: `${(currentIndex / total) * 100}%` }}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center ml-3">
          <button type="button" className="p-1 rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" title={isExpanded ? 'Fechar Jornada' : 'Abrir Jornada'} onClick={(e) => { e.stopPropagation(); if (onToggle) onToggle(); }}>
            {isExpanded ? <ChevronDown size={16} className="rotate-180" /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ProjectJourneySidebar: React.FC<ProjectJourneySidebarProps> = ({ 
  isOpen, 
  onClose,
  currentStep = 1,
  showDeviationAlert = false,
  showTopChrome = true,
  onStepClick,
  onOpenStageTransition,
  onGenerateActionPlan,
  step5Reviews = []
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast, ToastContainer } = useAppToast();
  const { isDark } = useTheme();
  // 🎯 SELETORES DE DADOS - Conectar ao estado do projeto
  const stage = useProjectStore(state => state.stage);
  const completedStages = useProjectStore(state => state.completedStages);
  const setStage = useProjectStore(state => state.setStage);
  const mapType = useProjectStore(state => state.mapType);
  const postIts = useProjectStore(state => state.postIts);
  const actions = useProjectStore(state => state.actions);
  const viewMode = useProjectStore(state => state.viewMode);
  const setViewMode = useProjectStore(state => state.setViewMode);
  const currentProjectId = useProjectStore(state => state.currentProjectId);
  const organizationId = useOrganizationId();
  const workspaceModeByViewMode: Record<'current_clean' | 'kaizen_work' | 'future_simulated', WorkspaceMode> = {
    current_clean: 'ATUAL',
    kaizen_work: 'KAIZEN',
    future_simulated: 'FUTURO',
  };
  
  // WorkspaceMode Context
  const { mode, setMode } = useWorkspaceMode();
  const effectiveMode = workspaceModeByViewMode[viewMode];

  useEffect(() => {
    // Keep Journey segmented control aligned with the actual canvas mode.
    if (mode !== effectiveMode) {
      setMode(effectiveMode);
    }
  }, [effectiveMode, mode, setMode]);
  
  // Obter cards de forma estável para evitar loop infinito
  const activeSheetId = useProjectStore(state => state.activeSheetId);
  const sheets = useProjectStore(state => state.sheets);
  const cards = React.useMemo(() => {
    if (!activeSheetId) return [];
    const activeSheet = sheets.find(s => s.id === activeSheetId);
    return activeSheet?.cards || [];
  }, [activeSheetId, sheets]);
  
  // Determinar step atual baseado no stage da store
  const currentStepId = stageToStepMap[stage];
  const currentIndex = STEPS.findIndex(s => s.id === currentStepId);
  
  // V87.1 — Overrides persistidos no useJourneyStore (substitui useState local)
  const taskOverrides = useJourneyStore((s) => s.taskOverrides);
  const toggleTask = useJourneyStore((s) => s.toggleTask);
  const [isGeneratingActionPlan, setIsGeneratingActionPlan] = useState(false);
  const [isBindingKpi, setIsBindingKpi] = useState(false);
  const [showLinkKpiModal, setShowLinkKpiModal] = useState(false);
  const [enterpriseActions, setEnterpriseActions] = useState<EnterpriseAction[]>([]);
  const [step5BlockBanner, setStep5BlockBanner] = useState<string | null>(null);
  const autoNormalizeSignatureRef = useRef<string | null>(null);
  const step5Evidence = useStep5Evidences(currentProjectId, step5Reviews);

  useEffect(() => {
    if (step5Evidence.canConclude) {
      setStep5BlockBanner(null);
    }
  }, [step5Evidence.canConclude]);

  const reloadEnterpriseActions = useCallback(async () => {
    if (!isOpen || !organizationId || !currentProjectId) {
      setEnterpriseActions([]);
      return;
    }
    try {
      const items = await ActionService.list(organizationId, { project_id: currentProjectId });
      setEnterpriseActions(items);
    } catch {
      setEnterpriseActions([]);
    }
  }, [currentProjectId, isOpen, organizationId]);

  useEffect(() => {
    void reloadEnterpriseActions();
  }, [reloadEnterpriseActions, stage]);

  const journeyActions = React.useMemo(() => {
    const mappedEnterpriseActions = enterpriseActions.map((action) => {
      const assignedName = action.assigned_name ?? (typeof action.metadata?.assignedName === 'string' ? action.metadata.assignedName : null);
      const metadataEffortRaw = action.metadata?.matrixEffort;
      const metadataImpactRaw = action.metadata?.matrixImpact;
      const metadataEffort = typeof metadataEffortRaw === 'number'
        ? metadataEffortRaw
        : typeof metadataEffortRaw === 'string' && metadataEffortRaw.trim() !== ''
          ? Number(metadataEffortRaw)
          : undefined;
      const metadataImpact = typeof metadataImpactRaw === 'number'
        ? metadataImpactRaw
        : typeof metadataImpactRaw === 'string' && metadataImpactRaw.trim() !== ''
          ? Number(metadataImpactRaw)
          : undefined;
      const effort = Number.isFinite(action.effort as number)
        ? (action.effort as number)
        : Number.isFinite(metadataEffort as number)
          ? metadataEffort
          : undefined;
      const impact = Number.isFinite(action.impact as number)
        ? (action.impact as number)
        : Number.isFinite(metadataImpact as number)
          ? metadataImpact
          : undefined;
      const effortLevel = effort == null
        ? undefined
        : effort <= 3
          ? effort as 1 | 2 | 3
          : effort < 33
            ? 1
            : effort < 66
              ? 2
              : 3;
      const impactLevel = impact == null
        ? undefined
        : impact <= 3
          ? impact as 1 | 2 | 3
          : impact < 33
            ? 1
            : impact < 66
              ? 2
              : 3;
      return {
        id: action.id,
        title: action.title,
        status: action.status === 'completed' ? 'done' : action.status === 'in_progress' ? 'doing' : action.status === 'cancelled' ? 'canceled' : 'todo',
        projectId: action.project_id ?? undefined,
        actionPlanId: action.plan_id ?? undefined,
        origin: 'canvas' as const,
        linkedNodeId: typeof action.metadata?.linkedNodeId === 'string' ? action.metadata.linkedNodeId : undefined,
        sourceComponentId: typeof action.metadata?.sourceComponentId === 'string' ? action.metadata.sourceComponentId : undefined,
        responsible: assignedName ?? undefined,
        responsavel: assignedName ?? undefined,
        responsibleUserId: action.assigned_to ?? undefined,
        deadline: action.due_date ?? undefined,
        prazo: action.due_date ?? undefined,
        impact,
        effort,
        impacto: impactLevel,
        esforco: effortLevel,
        createdAt: action.created_at,
        updatedAt: action.updated_at,
      };
    });
    // Keep Journey aligned with Central de Acoes: when enterprise actions exist,
    // they are the source of truth and local state only patches matching IDs.
    if (mappedEnterpriseActions.length > 0) {
      const localById = new Map(actions.map((action) => [action.id, action]));
      return mappedEnterpriseActions.map((enterprise) => {
        const local = localById.get(enterprise.id);
        if (!local) return enterprise;
        return {
          ...enterprise,
          ...local,
          priority: local.priority ?? enterprise.priority,
          impact: local.impact ?? enterprise.impact,
          effort: local.effort ?? enterprise.effort,
          impacto: local.impacto ?? enterprise.impacto,
          esforco: local.esforco ?? enterprise.esforco,
        };
      });
    }

    return actions;
  }, [actions, enterpriseActions]);

  const handleGenerateActionsFromOptimization = useCallback(async () => {
    if (!onGenerateActionPlan || isGeneratingActionPlan) return;
    setIsGeneratingActionPlan(true);
    try {
      await onGenerateActionPlan();
      await reloadEnterpriseActions();
      if (organizationId && currentProjectId) {
        void queryClient.invalidateQueries({ queryKey: ['step5-evidences-actions', organizationId, currentProjectId] });
      }
    } finally {
      setIsGeneratingActionPlan(false);
    }
  }, [currentProjectId, isGeneratingActionPlan, onGenerateActionPlan, organizationId, queryClient, reloadEnterpriseActions]);

  // Cards de processo do mapa (substitui vsm_process nodes)
  const processCardsWithCt = React.useMemo(() => {
    return cards.filter(c => {
      const isProcessCard = mapType === 'FACTORY'
        ? (c as any).vsmIcon === 'processo'
        : (c.tipo === 'atividade' || c.tipo === 'acao');
      return isProcessCard && (
        parseFloat((c as any).tc ?? '') > 0 ||
        parseFloat((c as any).vsmCT ?? '') > 0 ||
        parseFloat((c as any).vsmCO ?? '') > 0 ||
        parseFloat((c as any).vsmDisp ?? '') > 0 ||
        Number((c as any).lt) > 0 ||
        Number((c as any).tt) > 0
      );
    });
  }, [cards, mapType]);
  const allProcessCards = React.useMemo(() => {
    return cards.filter(c => {
      const isFactoryProcess = mapType === 'FACTORY' && (c as any).vsmIcon === 'processo';
      return isFactoryProcess || (mapType !== 'FACTORY' && (c.tipo === 'atividade' || c.tipo === 'acao'));
    });
  }, [cards, mapType]);
  // Cards kaizen/postit do mapa (substitui vsm_kaizen_burst nodes)
  const kaizenCards = React.useMemo(() => {
    if (mapType === 'FACTORY') {
      // Kaizen bursts = vsmKaizenProjection em process cards (bursts individuais)
      const withProjection = cards.filter(c => (c as any).vsmKaizenProjection != null);
      // Também: process cards membros de flow cell = Kaizen de Fluxo Contínuo foi projetado
      // Montar uma célula de fluxo contínuo É a evidência de engenharia de melhoria concluída
      const inFlowCell = cards.filter(c =>
        (c as any).vsmFlowCellId &&
        (c as any).vsmIcon === 'processo' &&
        !(c as any).vsmFlowCellBlocked &&
        c.status !== 'deletado'
      );
      const ids = new Set(withProjection.map(c => c.id));
      return [...withProjection, ...inFlowCell.filter(c => !ids.has(c.id))];
    }
    // Mapa de fluxo: cards postit/kaizen criados como elementos independentes
    return cards.filter(c => c.tipo === 'kaizen' || c.tipo === 'postit' || (c as any).vsmIcon === 'kaizen');
  }, [cards, mapType]);
  
  // Estado para controlar qual etapa está expandida (accordion)
  const [expandedStepId, setExpandedStepId] = useState<ProjectStep | null>(currentStepId);

  // Auto-expandir a etapa atual quando mudar
  useEffect(() => {
    setExpandedStepId(currentStepId);
  }, [currentStepId]);

  const forceOptimizationMode = useCallback(() => {
    setMode('KAIZEN');
    setViewMode('kaizen_work');
    useProjectStore.setState({ isKaizenMode: true });
  }, [setMode, setViewMode]);

  const forceFutureMode = useCallback(() => {
    setMode('FUTURO');
    setViewMode('future_simulated');
    useProjectStore.setState({ isKaizenMode: false });
  }, [setMode, setViewMode]);

  useEffect(() => {
    if (!isOpen || !currentProjectId || !onGenerateActionPlan || isGeneratingActionPlan) return;
    if (stage !== 'execution' && stage !== 'sustainment') return;

    const scopedActions = journeyActions.filter((action) =>
      Boolean(action.origin === 'canvas' || action.linkedNodeId || action.projectId || action.actionPlanId),
    );
    const dynamicGenerated = scopedActions.filter((action) => Boolean(action.sourceComponentId));
    if (dynamicGenerated.length === 0) return;

    const seen = new Set<string>();
    const hasDuplicateSourceComponent = dynamicGenerated.some((action) => {
      const key = `${action.projectId ?? currentProjectId ?? 'sem-projeto'}::${action.sourceComponentId}`;
      if (seen.has(key)) return true;
      seen.add(key);
      return false;
    });
    if (!hasDuplicateSourceComponent) return;

    const signature = `${currentProjectId}:${stage}:${scopedActions.length}`;
    if (autoNormalizeSignatureRef.current === signature) return;
    autoNormalizeSignatureRef.current = signature;

    void handleGenerateActionsFromOptimization();
  }, [
    currentProjectId,
    handleGenerateActionsFromOptimization,
    isGeneratingActionPlan,
    isOpen,
    journeyActions,
    onGenerateActionPlan,
    stage,
  ]);

  const previousStageRef = useRef<ProjectStage>(stage);

  // V171.1 — Ao entrar na Etapa 2, forçar OTIMIZAÇÃO; ao entrar na Etapa 3, forçar FUTURO
  useEffect(() => {
    const previousStage = previousStageRef.current;
    previousStageRef.current = stage;

    if (previousStage !== 'kaizen' && stage === 'kaizen') {
      forceOptimizationMode();
    }
    if (previousStage !== 'planning' && stage === 'planning') {
      forceFutureMode();
    }
  }, [forceOptimizationMode, forceFutureMode, stage]);

  // Determinar modos disponíveis baseado nas etapas completadas
  const availableModes: { mode: WorkspaceMode; unlocked: boolean; stage: ProjectStage }[] = [
    { 
      mode: 'ATUAL', 
      unlocked: true, // Sempre disponível
      stage: 'diagnosis'
    },
    { 
      mode: 'KAIZEN', 
      unlocked: completedStages.includes('diagnosis') || stage === 'kaizen' || stage === 'planning' || stage === 'execution' || stage === 'sustainment',
      stage: 'kaizen'
    },
    { 
      mode: 'FUTURO', 
      // Desbloqueado ao entrar na Etapa 3 (planning) ou estágios posteriores
      unlocked: completedStages.includes('kaizen') || stage === 'planning' || stage === 'execution' || stage === 'sustainment',
      stage: 'planning'
    }
  ];

  const handleModeChange = (newMode: WorkspaceMode, unlocked: boolean) => {
    if (!unlocked) return;

    setMode(newMode);

    // Sincronizar com viewMode
    const viewModeMap: Record<WorkspaceMode, 'current_clean' | 'kaizen_work' | 'future_simulated'> = {
      ATUAL: 'current_clean',
      KAIZEN: 'kaizen_work',
      FUTURO: 'future_simulated'
    };
    setViewMode(viewModeMap[newMode]);

    // Sincronizar isKaizenMode: ativo apenas no modo Oportunidades (KAIZEN)
    useProjectStore.setState({ isKaizenMode: newMode === 'KAIZEN' });
  };

  const modeConfig: Record<WorkspaceMode, {
    label: string;
    description: string;
    lockedReason: string;
    color: string;
    bgColor: string;
    borderColor: string;
    icon: React.ElementType;
    activeSolid: string;
    activeRing: string;
  }> = {
    ATUAL: {
      label: 'ATUAL',
      description: 'Processo mapeado',
      lockedReason: '',
      color: 'text-blue-700 dark:text-blue-300',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-300 dark:border-blue-700',
      activeSolid: 'bg-blue-600 border-blue-600 text-white shadow-md ring-2 ring-blue-400 ring-offset-1',
      activeRing: 'ring-2 ring-blue-400',
      icon: MapIcon
    },
    KAIZEN: {
      label: 'OTIMIZAÇÃO',
      description: 'Engenharia de Melhoria e ideias priorizadas',
      lockedReason: 'Complete a Etapa 1 → Diagnóstico para desbloquear',
      color: 'text-amber-700 dark:text-amber-300',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20',
      borderColor: 'border-amber-300 dark:border-amber-700',
      activeSolid: 'bg-amber-500 border-amber-500 text-white shadow-md ring-2 ring-amber-400 ring-offset-1',
      activeRing: 'ring-2 ring-amber-400',
      icon: Zap
    },
    FUTURO: {
      label: 'FUTURO',
      description: 'Melhorias aplicadas',
      lockedReason: 'Complete a Etapa 2 → Kaizen para desbloquear',
      color: 'text-emerald-700 dark:text-emerald-300',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
      borderColor: 'border-emerald-300 dark:border-emerald-700',
      activeSolid: 'bg-emerald-600 border-emerald-600 text-white shadow-md ring-2 ring-emerald-400 ring-offset-1',
      activeRing: 'ring-2 ring-emerald-400',
      icon: Target
    }
  };

  if (!isOpen) return null;

  const shellClassName = !isDark
    ? 'border-l border-slate-200 bg-white'
    : 'border-l border-slate-700 bg-slate-800';

  const headerClassName = !isDark
    ? 'border-b border-slate-200 bg-white'
    : 'border-b border-slate-700 bg-slate-800';

  const getStepStatus = (step: StepConfig): 'completed' | 'current' | 'upcoming' => {
    const mappedStage = stepToStageMap[step.id];
    if (completedStages.includes(mappedStage)) return 'completed';
    if (step.id === currentStepId) return 'current';
    return 'upcoming';
  };

  const handleStepClick = (step: ProjectStep) => {
    const previousStage = stage;
    const mappedStage = stepToStageMap[step];
    setStage(mappedStage);
    setExpandedStepId(step);
    if (step === 'design' && previousStage !== 'kaizen') {
      forceOptimizationMode();
    }
    if (onStepClick) onStepClick(step);
  };

  // Toggle accordion
  const toggleAccordion = (stepId: ProjectStep) => {
    setExpandedStepId(prev => prev === stepId ? null : stepId);
  };

  // V87.1 — Toggle persiste no useJourneyStore (antes: useState local)
  const toggleCheckbox = (checklistItemId: string) => {
    toggleTask(checklistItemId);
  };

  const buildActionPlanRoute = useCallback((view: 'list' | 'matrix', focus?: 'responsavel' | 'prazo') => {
    const actionPlanId = journeyActions.find((action) => action.actionPlanId)?.actionPlanId;
    const params = new URLSearchParams();
    if (actionPlanId) {
      params.set('planId', actionPlanId);
    } else if (currentProjectId) {
      params.set('project_id', currentProjectId);
    }
    if (focus) params.set('actionFocus', focus);
    const query = params.toString();
    const path = view === 'matrix' ? '/acoes/matriz' : '/acoes';
    return query ? `${path}?${query}` : path;
  }, [currentProjectId, journeyActions]);

  const handleStep5TargetClick = useCallback((target: Step5CtaTarget) => {
    if (!currentProjectId) return;
    if (target === 'actions') {
      navigate(buildActionPlanRoute('list'));
      return;
    }
    if (target === 'kpi') {
      setShowLinkKpiModal(true);
      return;
    }
    navigate(`/projetos/${currentProjectId}`, { state: { step5Focus: 'audit', openAuditModal: true } });
  }, [buildActionPlanRoute, currentProjectId, navigate]);

  const handleChecklistItemClick = useCallback((stepId: ProjectStep, checklistItemId: string) => {
    if (stepId !== 'execucao') {
      if (stepId === 'sustentacao') {
        if (checklistItemId === 'monitor-execution') {
          navigate(buildActionPlanRoute('list'));
          return;
        }
        if (checklistItemId === 'measure-results') {
          handleStep5TargetClick('kpi');
          return;
        }
        if (checklistItemId === 'sustain-improvements') {
          handleStep5TargetClick('audit');
          return;
        }
      }
      toggleCheckbox(checklistItemId);
      return;
    }

    if (checklistItemId === 'prioritize') {
      navigate(buildActionPlanRoute('matrix'));
      return;
    }

    if (checklistItemId === 'assign-responsible') {
      navigate(buildActionPlanRoute('list', 'responsavel'));
      return;
    }

    if (checklistItemId === 'set-deadlines') {
      navigate(buildActionPlanRoute('list', 'prazo'));
      return;
    }

    navigate(buildActionPlanRoute('list'));
  }, [buildActionPlanRoute, handleStep5TargetClick, navigate, toggleCheckbox]);

  const handleBindKpi = useCallback(async (kpi: CanonicalKpiOption, targetValue: number | null) => {
    if (!currentProjectId) return;

    const nowIso = new Date().toISOString();
    setIsBindingKpi(true);

    try {
      useProjectStore.setState((state) => ({
        projects: state.projects.map((project) => {
          if (project.id !== currentProjectId) return project;
          const existing = project.estado_atual?.kpis ?? [];
          const nextKpis = [
            ...existing.filter((item) => item.name.trim().toLowerCase() !== kpi.name.trim().toLowerCase()),
            {
              id: kpi.id,
              name: kpi.name,
              unit: kpi.unit,
              type: kpi.type,
              currentValue: kpi.initialValue,
              targetValue,
              lastReadingDate: nowIso,
            },
          ];

          return {
            ...project,
            estado_atual: {
              ...project.estado_atual,
              kpis: nextKpis,
            },
          };
        }),
      }));

      if (organizationId) {
        const existingIndicators = await IndicatorService.list(organizationId, { project_id: currentProjectId, search: kpi.name });
        const exact = existingIndicators.find((item) => item.name.trim().toLowerCase() === kpi.name.trim().toLowerCase());

        if (!exact) {
          const created = await IndicatorService.create(organizationId, {
            name: kpi.name,
            type: kpi.type,
            unit: kpi.unit,
            direction: kpi.direction,
            status: 'active',
            frequency: 'daily',
            current_value: kpi.initialValue,
            target_value: targetValue,
            project_id: currentProjectId,
            visibility_scope: 'project',
            tags: ['stp_canonico', 'journey_step5'],
          });
          await IndicatorService.recordSnapshot(organizationId, created.id, kpi.initialValue);
        } else if (targetValue != null && exact.target_value !== targetValue) {
          // KPI já existia mas usuário definiu/atualizou target nesta ativação
          await IndicatorService.update(organizationId, exact.id, { target_value: targetValue });
        }
      }

      await queryClient.invalidateQueries({ queryKey: ['indicators'] });
      setShowLinkKpiModal(false);
      showToast('success', 'KPI vinculado com sucesso à Gestão à Vista!');
    } catch {
      showToast('error', 'Falha ao vincular KPI neste projeto.');
    } finally {
      setIsBindingKpi(false);
    }
  }, [currentProjectId, organizationId, queryClient, showToast]);

  // 🎯 Handler para confirmar conclusão da etapa e avançar
  const handleCompleteStage = async (stepId: ProjectStep) => {
    if (stepId === 'sustentacao' && !step5Evidence.canConclude) {
      setStep5BlockBanner(step5Evidence.blockMessage);
      return;
    }

    const checklistItems = getChecklistItems(stepId);
    const completedCount = checklistItems.filter(item => {
      const isManuallyChecked = taskOverrides[item.id];
      return isManuallyChecked || item.isComplete;
    }).length;
    const totalCount = checklistItems.length;
    const isFullyComplete = completedCount === totalCount;

    if (!isFullyComplete) {
      const confirmed = window.confirm(
        `Ainda existem ${totalCount - completedCount} tarefa(s) pendente(s) nesta etapa.\\n\\nDeseja avançar mesmo assim?`
      );
      if (!confirmed) return;
    }

    // Obter próxima etapa
    const currentStepIndex = STEPS.findIndex(s => s.id === stepId);
    const nextStep = STEPS[currentStepIndex + 1];
    
    if (nextStep) {
      const currentStage = stepToStageMap[stepId];
      const nextStage = stepToStageMap[nextStep.id];

      if (currentStage === 'planning' && nextStage === 'execution' && onGenerateActionPlan) {
        useProjectStore.getState().completeStage(currentStage);
        setStage(nextStage);
        setExpandedStepId(nextStep.id);
        await onGenerateActionPlan();
        return;
      }

      if (currentStage === 'sustainment') {
        useProjectStore.getState().completeStage(currentStage);
        return;
      }
      
      // 🎯 Abrir modal de transição guiado
      if (onOpenStageTransition) {
        onOpenStageTransition(currentStage, nextStage);
      } else {
        // Fallback: avançar diretamente
        useProjectStore.getState().completeStage(currentStage);
        handleStepClick(nextStep.id);
      }
    }
  };

  // Função para obter checklist de cada etapa (integrado do StageChecklist)
  const getChecklistItems = (stepId: ProjectStep) => {
    const mappedStage = stepToStageMap[stepId];

    switch (mappedStage) {
      case 'diagnosis':
        const hasEndCard = cards.some(c => c.tipo === 'card_final');
        const endCard = cards.find(c => c.tipo === 'card_final');
        const isFlowConnected = hasEndCard && cards.some(c => 
          c.conexoes_saida && c.conexoes_saida.some(conn => conn.targetId === endCard.id)
        );
        
        const processCards = cards.filter(c =>
          (c.tipo === 'atividade' || c.tipo === 'acao') && c.status !== 'removido'
        );
        const cardsWithMetrics = processCards.filter(c => 
          (c.lt && c.lt > 0) || (c.tt && c.tt > 0)
        );
        const metricsProgress = processCards.length > 0 
          ? `${cardsWithMetrics.length}/${processCards.length}`
          : '0/0';

        const isFactoryMap = mapType === 'FACTORY';

        // Validação automática: cards de processo com TC > 0 (campo tc no MapaPage FACTORY)
        const allProcessHaveCt =
          allProcessCards.length > 0 &&
          processCardsWithCt.length === allProcessCards.length;
        const metricsCompleteAuto =
          (processCards.length > 0 && cardsWithMetrics.length === processCards.length) ||
          allProcessHaveCt;
        const metricsProgressLabel =
          allProcessCards.length > 0 && isFactoryMap
            ? `${processCardsWithCt.length}/${allProcessCards.length}`
            : metricsProgress;

        return [
          {
            id: 'map-flow',
            label: 'Mapear Fluxo Atual',
            description: isFlowConnected 
              ? '✅ Fluxo conectado ao card FIM' 
              : 'Conecte START → Atividades → END',
            isComplete: isFlowConnected,
          },
          {
            id: 'define-metrics',
            label: 'Definir Métricas',
            description: (
              <>
                Preencha tempos {isFactoryMap ? (
                  <span className="font-mono text-cyan-600 dark:text-cyan-400 font-bold">LT/TC</span>
                ) : (
                  <>(<span className="font-mono text-cyan-600 dark:text-cyan-400 font-bold">TP</span>, <span className="font-mono text-cyan-600 dark:text-cyan-400 font-bold">TRA</span>)</>
                )} • {metricsProgressLabel} cards
              </>
            ),
            // V87.1: auto-concluir se TODOS os vsm_process têm cycleTime > 0
            isComplete: metricsCompleteAuto,
          },
        ];

      case 'kaizen':
        const hasPostIts = postIts.length > 0;
        const isFactoryKaizen = mapType === 'FACTORY';
        // Para mapas FACTORY: kaizen bursts (vsmKaizenProjection) = oportunidade mapeada E impacto definido
        // Para fluxo: ideias de melhoria e linkedActionId
        const quantifiedPostIts = postIts.filter(p =>
          (p.impactos || []).some(impact => impact.targetNodeId && (impact.tipo === 'ELIMINAR' || Number(impact.reducao) > 0)),
        );
        const linkedImpactCount = postIts.filter(p => p.linkedActionId).length;
        const impactCount = Math.max(quantifiedPostIts.length, linkedImpactCount);
        const hasImpactValues = isFactoryKaizen
          ? kaizenCards.length > 0
          : impactCount > 0;

        // Contar cards kaizen/postit do mapa
        const hasKaizenCards = kaizenCards.length > 0;
        const opportunitiesIsComplete = isFactoryKaizen ? hasKaizenCards : (hasPostIts || hasKaizenCards);

        const opportunitiesDescription = isFactoryKaizen
          ? hasKaizenCards
            ? `✅ ${kaizenCards.length} posto${kaizenCards.length > 1 ? 's' : ''} com Kaizen Burst`
            : 'Adicionar Kaizen Bursts aos postos no canvas'
          : hasKaizenCards
            ? `✅ ${kaizenCards.length} kaizen${kaizenCards.length > 1 ? 's' : ''} no mapa`
            : hasPostIts
              ? `✅ ${postIts.length} ideias de melhoria criadas`
              : 'Criar ideias de melhoria ou Kaizens no mapa';

        return [
          {
            id: 'map-opportunities',
            label: 'Mapear Oportunidades',
            description: opportunitiesDescription,
            isComplete: opportunitiesIsComplete,
          },
          {
            id: 'define-impact',
            label: isFactoryKaizen ? 'Projetar Melhoria' : 'Definir Impacto',
            description: isFactoryKaizen
              ? hasImpactValues
                ? `✅ Projeções definidas em ${kaizenCards.length} posto${kaizenCards.length > 1 ? 's' : ''}`
                : 'Configurar projeções de T/C, Setup e Disponibilidade'
              : hasImpactValues
                ? `✅ ${impactCount} ideia${impactCount > 1 ? 's' : ''} com impacto definido`
                : 'Vincular ideias de melhoria aos cards e quantificar redução',
            isComplete: hasImpactValues,
          },
        ];

      case 'planning':
        const cardsToEliminate = cards.filter(c =>
          postIts.some(p =>
            (p.impactos || []).some(impact => impact.targetNodeId === c.id && impact.tipo === 'ELIMINAR')
            || (p.linkedCardId === c.id && p.action === 'eliminar')
          )
        );
        const hasEliminatedCards = cardsToEliminate.length > 0;
        // V201: para mapas FACTORY, "Gerar Mapa Futuro" = entrou no modo future_simulated (ação automática da Etapa 3)
        const vsmFuturoHideCards = mapType === 'FACTORY'
          ? cards.filter(c => (c as any).vsmFuturoHide || (c as any).vsmFlowCellBlocked)
          : [];
        const hasFutureMap = hasEliminatedCards
          || vsmFuturoHideCards.length > 0
          || viewMode === 'future_simulated';

        return [
          {
            id: 'generate-future-map',
            label: 'Gerar Mapa Futuro',
            description: hasFutureMap
              ? mapType === 'FACTORY'
                ? `✅ ${vsmFuturoHideCards.length > 0 ? vsmFuturoHideCards.length + ' cards ocultos no mapa futuro' : cardsToEliminate.length + ' atividades a eliminar'}`
                : cardsToEliminate.length > 0
                  ? `✅ ${cardsToEliminate.length} atividade${cardsToEliminate.length > 1 ? 's' : ''} removida${cardsToEliminate.length > 1 ? 's' : ''} do mapa futuro`
                  : '✅ Mapa futuro gerado'
              : 'Ocultar atividades eliminadas e gerar fluxo otimizado',
            isComplete: hasFutureMap,
          },
          {
            id: 'calculate-new-leadtime',
            label: 'Calcular Novo Lead Time',
            description: hasFutureMap
              ? `✅ Lead Time futuro projetado`
              : 'Recalcular tempo total do processo otimizado',
            isComplete: hasFutureMap,
          },
          {
            id: 'validate-improvements',
            label: 'Validar Melhorias',
            description: hasFutureMap
              ? '✅ Ganhos projetados validados'
              : 'Revisar redução de tempo e desperdícios',
            isComplete: hasFutureMap,
          },
        ];

      case 'execution':
        const actionPlanActions = journeyActions.filter(a => a.origin === 'canvas' || a.linkedNodeId || a.projectId || a.actionPlanId);
        // A higienização semântica acontece na Central de Ações (AcoesPage),
        // que é a fonte de verdade. Aqui só removemos canceladas e refletimos
        // o que está no banco — auto + manual juntas.
        const scopedActionPlanActions = actionPlanActions;
        const executionActions = scopedActionPlanActions.filter(a => a.status !== 'canceled');
        const totalActions = executionActions.length;
        const hasActions = totalActions > 0;
        const actionsWithResponsible = executionActions.filter(a => Boolean((a.responsavel ?? a.responsible ?? '').trim() || a.responsibleUserId)).length;
        const actionsWithDeadline = executionActions.filter(a => Boolean(a.prazo ?? a.deadline)).length;
        const actionsWithPriority = executionActions.filter(a =>
          a.priority != null || a.impacto != null || a.esforco != null || a.impact != null || a.effort != null
        ).length;

        return [
          {
            id: 'register-actions',
            label: 'Registrar Ações',
            description: hasActions
              ? `✅ ${scopedActionPlanActions.length} ações cadastradas`
              : 'Converter ideias de melhoria em ações',
            isComplete: hasActions,
          },
          {
            id: 'assign-responsible',
            label: 'Atribuir Responsáveis',
            description: totalActions > 0
              ? `${actionsWithResponsible}/${totalActions} ações com responsável`
              : 'Definir quem executará cada ação',
            isComplete: totalActions > 0 && actionsWithResponsible === totalActions,
          },
          {
            id: 'set-deadlines',
            label: 'Definir Prazos',
            description: totalActions > 0
              ? `${actionsWithDeadline}/${totalActions} ações com prazo`
              : 'Estabelecer cronograma de execução',
            isComplete: totalActions > 0 && actionsWithDeadline === totalActions,
          },
          {
            id: 'prioritize',
            label: 'Priorizar Ações',
            description: totalActions > 0
              ? `${actionsWithPriority}/${totalActions} ações priorizadas`
              : 'Ordenar por impacto vs. esforço',
            isComplete: totalActions > 0 && actionsWithPriority === totalActions,
          },
        ];

      case 'sustainment':
        return [
          {
            id: 'monitor-execution',
            label: 'Monitorar Execução',
            description: step5Evidence.checks[0].detail,
            isComplete: step5Evidence.checks[0].isValid,
            ctaLabel: step5Evidence.checks[0].ctaLabel,
            ctaTarget: step5Evidence.checks[0].ctaTarget,
          },
          {
            id: 'measure-results',
            label: 'Medir Resultados',
            description: step5Evidence.checks[1].detail,
            isComplete: step5Evidence.checks[1].isValid,
            ctaLabel: step5Evidence.checks[1].ctaLabel,
            ctaTarget: step5Evidence.checks[1].ctaTarget,
          },
          {
            id: 'sustain-improvements',
            label: 'Sustentar Ganhos',
            description: step5Evidence.checks[2].detail,
            isComplete: step5Evidence.checks[2].isValid,
            ctaLabel: step5Evidence.checks[2].ctaLabel,
            ctaTarget: step5Evidence.checks[2].ctaTarget,
          },
        ];

      default:
        return [];
    }
  };

  // If sidebar is closed and not inline, render nothing
  if (!isOpen) return null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {showTopChrome && (
        <div className={`px-4 py-1.5 ${!isDark ? 'border-b border-slate-200' : 'border-b border-slate-800'}`}>
          <div className={`h-1 rounded-full overflow-hidden ${showDeviationAlert ? 'ring-1 ring-amber-400/80' : ''} ${!isDark ? 'bg-slate-100' : 'bg-slate-800'}`}>
            <div 
              className={`h-full transition-all duration-500 rounded-full ${showDeviationAlert ? 'bg-amber-400' : 'bg-cyan-400'}`}
              style={{ width: `${((currentIndex + 1) / STEPS.length) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* 🎯 WORKSPACE MODES - Visualização do Mapa */}
      <div className={`px-3 py-2 ${headerClassName}`}>
        {/* SegmentedControl */}
        <div
          className={`flex rounded-sm p-0.5 ${!isDark ? 'bg-slate-100' : 'bg-slate-800/50'}`}
          style={{ width: '100%' }}
        >
          {availableModes.map(({ mode: modeOption, unlocked }, idx) => {
            const config = modeConfig[modeOption];
            const isActive = effectiveMode === modeOption;
            const isCurrentSnapshotLocked = modeOption === 'ATUAL' && stage !== 'diagnosis';
            const ModeIcon = config.icon;
            return (
              <button
                key={modeOption}
                onClick={() => handleModeChange(modeOption, unlocked)}
                disabled={!unlocked}
                title={unlocked ? config.description : `🔒 ${config.lockedReason}`}
                className={`flex-1 flex items-center justify-center gap-1.5 h-7 px-2 rounded-sm transition-all duration-150 ${
                  isActive && unlocked
                    ? !isDark ? 'bg-white shadow-sm text-slate-900 border border-slate-200/50' : 'bg-slate-700 shadow-sm text-white border border-slate-600/50'
                    : 'bg-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                } ${!unlocked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {unlocked
                  ? <ModeIcon size={12} className={isActive ? 'text-cyan-500' : 'text-inherit'} />
                  : <Lock size={10} className="text-inherit" />
                }
                <span className="font-mono text-[9.5px] font-semibold uppercase tracking-wider whitespace-nowrap">
                  {config.label}
                </span>
                {unlocked && isCurrentSnapshotLocked && (
                  <Lock size={9} className={isActive ? 'text-cyan-500' : 'text-inherit'} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Steps */}
      <div className={`flex-1 overflow-y-auto p-3 pb-6 space-y-2 ${shellClassName}`}>
        {STEPS.map((step, index) => {
          const status = getStepStatus(step);
          const Icon = step.icon;
          const isExpanded = expandedStepId === step.id;
          const checklistItems = getChecklistItems(step.id);
          const completedCount = checklistItems.filter(item => {
            // V87.1: taskOverrides persiste via useJourneyStore
            const isManuallyChecked = taskOverrides[item.id];
            return isManuallyChecked || item.isComplete;
          }).length;
          const totalCount = checklistItems.length;

          return (
            <div key={step.id} className="space-y-1">
              {/* Header do Step */}
              <button
                onClick={() => {
                  handleStepClick(step.id);
                  toggleAccordion(step.id);
                }}
                className={`w-full flex items-center gap-2 p-2 transition-all ${
                  status === 'current'
                    ? !isDark 
                      ? 'bg-slate-50 border border-slate-300 border-l-[3px] border-l-[#06b6d4] shadow-sm' 
                      : 'bg-slate-800/80 border border-slate-700 border-l-[3px] border-l-[#06b6d4] shadow-sm'
                    : status === 'completed'
                    ? !isDark ? 'bg-white border border-slate-200' : 'bg-slate-800/40 border border-slate-700/50'
                    : !isDark ? 'bg-white border border-slate-200' : 'bg-slate-800/40 border border-slate-700/50'
                }`}
              >
                {/* Icon */}
                <div className="flex-shrink-0">
                  {status === 'completed' ? (
                    <CheckCircle2 size={15} className="text-cyan-300" />
                  ) : status === 'current' ? (
                    <div className="w-4 h-4 rounded-full bg-cyan-500 flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                    </div>
                  ) : (
                    <Circle size={15} className={!isDark ? 'text-slate-400' : 'text-slate-600'} />
                  )}
                </div>

                <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border ${!isDark ? 'border-slate-200 bg-white text-slate-600' : 'border-slate-700 bg-slate-900 text-slate-400'}`}>
                  <Icon size={11} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex flex-col gap-0.5 mb-0.5">
                    <span className={`font-mono text-[9px] font-bold tracking-wider ${
                      status === 'current'
                        ? 'text-cyan-500 dark:text-cyan-400'
                        : status === 'completed'
                        ? 'text-emerald-500 dark:text-emerald-400'
                        : !isDark ? 'text-slate-400' : 'text-slate-500'
                    }`}>
                      ETAPA {index + 1}
                    </span>
                  </div>
                  <h4 className={`text-[12px] font-semibold leading-tight ${
                    status === 'current'
                      ? !isDark ? 'text-slate-900' : 'text-slate-100'
                      : status === 'completed'
                      ? !isDark ? 'text-slate-800' : 'text-slate-200'
                      : !isDark ? 'text-slate-600' : 'text-slate-400'
                  }`}>
                    {step.label}
                  </h4>
                  <p className={`font-mono text-[9px] ${!isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    {completedCount}/{totalCount} critérios
                  </p>
                </div>

                {/* Chevron */}
                <ChevronDown 
                  size={14} 
                  className={`text-slate-400 dark:text-slate-500 transition-transform duration-200 ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Checklist expandível */}
              <div 
                className={`overflow-hidden transition-all duration-200 ${
                  isExpanded ? 'max-h-[400px]' : 'max-h-0'
                }`}
              >
                <div className="flex flex-col ml-[28px] pr-1">
                  {checklistItems.map((item) => {
                    const isManuallyChecked = taskOverrides[item.id];
                    const isChecked = isManuallyChecked || item.isComplete;
                    const isExecutionShortcut = step.id === 'execucao';
                    const showGenerateInlineAction =
                      step.id === 'execucao' &&
                      status === 'current' &&
                      item.id === 'register-actions' &&
                      Boolean(onGenerateActionPlan);
                    
                    return (
                      <div key={item.id} className="border-b last:border-b-0 border-transparent">
                        <button
                          onClick={() => handleChecklistItemClick(step.id, item.id)}
                          title={isExecutionShortcut || step.id === 'sustentacao' ? 'Abrir tela para executar esta etapa' : 'Marcar tarefa da Jornada'}
                          className={`w-full flex items-start gap-2 py-1.5 px-0.5 transition-all ${
                            isChecked
                              ? !isDark ? 'border-emerald-100 hover:bg-emerald-50/30' : 'border-emerald-900/30 hover:bg-emerald-900/10'
                              : !isDark ? 'border-slate-100 hover:bg-slate-50/50' : 'border-slate-800/40 hover:bg-slate-800/20'
                          }`}
                        >
                          <div className="shrink-0 mt-0.5 flex items-center justify-center">
                            {isChecked ? (
                              <CheckCircle2 size={13} className="text-emerald-500" strokeWidth={2.5} />
                            ) : (
                              <Circle size={13} className="text-slate-300 dark:text-slate-600" strokeWidth={2} />
                            )}
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <p className={`text-[10px] font-semibold truncate ${
                              isChecked ? 'text-emerald-700 dark:text-emerald-400 line-through opacity-80' : !isDark ? 'text-slate-700' : 'text-slate-300'
                            }`}>
                              {item.label}
                            </p>
                            <p className={`font-mono text-[9px] leading-tight ${!isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                              {item.description}
                            </p>
                          </div>
                        </button>

                        {showGenerateInlineAction && (
                          <button
                            type="button"
                            disabled={isGeneratingActionPlan}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleGenerateActionsFromOptimization();
                            }}
                            className={`mb-1 ml-[22px] flex w-[calc(100%-22px)] items-center justify-center gap-1 rounded-sm border px-2 py-1 text-[9px] font-semibold tracking-wide transition-colors disabled:cursor-wait disabled:opacity-60 ${!isDark ? 'border-slate-300 bg-white text-slate-600 hover:border-cyan-400 hover:text-cyan-700' : 'border-slate-600 bg-slate-900 text-slate-300 hover:border-cyan-400 hover:text-cyan-300'}`}
                          >
                            <ClipboardList size={11} />
                            {isGeneratingActionPlan ? 'Atualizando...' : 'Atualizar acoes do plano'}
                            <ArrowRight size={11} strokeWidth={2.5} />
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {/* KPIs Contextuais na Etapa Sustentação */}
                  {step.id === 'sustentacao' && status === 'current' && currentProjectId && (
                    <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700">
                      {step5BlockBanner && (
                        <div className="mb-3 rounded-sm border border-red-500/30 bg-red-950/30 px-3 py-2 text-[10px] leading-snug text-red-200">
                          {step5BlockBanner}
                        </div>
                      )}
                      <div className="mb-3 px-1 flex items-center justify-between text-xs font-semibold text-[var(--text-primary)]">
                        <span>Gestão à Vista</span>
                      </div>
                      
                      <div className="mb-4">
                        <ContextualKPI 
                          projectId={currentProjectId} 
                          organizationId={organizationId}
                          layout="sidebar"
                          onRequestBindKpi={() => setShowLinkKpiModal(true)}
                        />
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStep5TargetClick('kpi');
                        }}
                        className="w-full flex items-center justify-center gap-2 rounded-sm border border-emerald-700 bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-800"
                      >
                        <TrendingUp size={16} />
                        <span>Ver Performance & ROI</span>
                      </button>
                    </div>
                  )}

                  {/* Botão de avanço */}
                  {status === 'current' && (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleCompleteStage(step.id);
                        }}
                        className={`w-full flex items-center justify-center gap-1.5 rounded-sm border px-3 py-2 text-[11px] font-bold tracking-wider transition-colors ${
                          completedCount === totalCount
                            ? !isDark ? 'bg-cyan-500 border-cyan-500 text-white hover:bg-cyan-600' : 'bg-cyan-500 border-cyan-500 text-slate-900 hover:bg-cyan-400'
                            : !isDark ? 'bg-transparent border-cyan-500/40 text-cyan-600 hover:bg-cyan-50' : 'bg-transparent border-cyan-500/40 text-cyan-400 hover:bg-cyan-950/30'
                        }`}
                      >
                        {step.id === 'planejamento' ? 'CONVERTER EM AÇÕES' : step.id === 'sustentacao' ? 'CONCLUIR JORNADA' : 'CONCLUIR ETAPA'}
                        <ArrowRight size={12} strokeWidth={2.5} />
                      </button>
                      {completedCount < totalCount && (
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 text-center mt-2">
                          ⚠️ Tarefas pendentes (<span className="font-mono">{completedCount}/{totalCount}</span>)
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <LinkKpiModal
        open={showLinkKpiModal && Boolean(currentProjectId)}
        projectId={currentProjectId ?? ''}
        onClose={() => setShowLinkKpiModal(false)}
        onActivateKpi={handleBindKpi}
        isBinding={isBindingKpi}
      />
      <ToastContainer />
    </div>
  );
};
